import React, { useState } from 'react';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import {
   MessageCircle, Mail, Phone, Send, ChevronDown,
  Video, Clock, CheckCircle2, Star, BookOpen, Zap, Calendar,
  ExternalLink, Copy, HelpCircle, FileText, Link as LinkIcon, Download,
  Code, Terminal, Shield, Wrench, Info, LifeBuoy, Settings, GraduationCap,
  Briefcase, CreditCard, Inbox, AlertCircle
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import ConfirmModal from '@/components/shared/ConfirmModal';

const faqs = [
  { q: 'How do I access class recordings?', a: 'Class recordings are available in the "Class Recordings" section within 24 hours of the live session. All recordings are stored permanently.' },
  { q: 'Can I download the notes and resources?', a: 'Yes! All notes and resources are available for download from the Notes and Resources sections. PDFs, code files, and presentations are included.' },
  { q: 'How do I submit an assignment?', a: "Go to the Assignments section, find the assignment, and click the \"Submit\" button to upload your work. You'll get a confirmation email once submitted." },
  { q: 'What is the placement support process?', a: 'Our placement team assists with resume reviews, mock interviews, and direct referrals to our 120+ hiring partners. Access the Placement section to get started.' },
  { q: 'How do I schedule a 1:1 with my mentor?', a: 'Use the "Schedule Meeting" button below to book a slot directly from your mentor\'s calendar. Sessions are 30–60 mins and happen via Google Meet.' },
  { q: 'How do I upgrade or renew my subscription?', a: 'Contact us via email or call our support line. Our team will assist you with upgrades and renewals within 24 hours.' },
];

const meetingSlots = [
  { time: 'Today, 3:00 PM', available: true },
  { time: 'Today, 5:00 PM', available: false },
  { time: 'Tomorrow, 10:00 AM', available: true },
  { time: 'Tomorrow, 2:00 PM', available: true },
  { time: 'Wed, 11:00 AM', available: true },
  { time: 'Wed, 4:00 PM', available: false },
];

// Admins type a lucide icon name into support_resources.icon (e.g. "HelpCircle").
// Only a curated set is bundled; anything else falls back to the category icon.
const ICONS = {
  HelpCircle, BookOpen, Video, FileText, Link: LinkIcon, Download, Code, Terminal,
  Shield, Wrench, Info, LifeBuoy, Settings, GraduationCap, Briefcase, Zap,
  MessageCircle, Mail, Phone, Calendar, CreditCard,
};
const CATEGORY_ICONS = {
  General: HelpCircle, Technical: Terminal, Billing: CreditCard,
  'Course Content': BookOpen, Other: Info,
};
const resourceIcon = (r) => ICONS[r.icon?.trim()] || CATEGORY_ICONS[r.category] || LifeBuoy;

// Turn bare URLs in admin-written content into links; everything else stays text.
const URL_RE = /(https?:\/\/[^\s]+)/g;
function Linkified({ text }) {
  return String(text || '').split(URL_RE).map((part, i) =>
    /^https?:\/\//.test(part)
      ? <a key={i} href={part} target="_blank" rel="noopener noreferrer" className="text-primary font-medium underline underline-offset-2 break-all">{part}</a>
      : <React.Fragment key={i}>{part}</React.Fragment>
  );
}

const TICKET_STATUS = {
  open:        { label: 'Open',        tone: 'bg-amber-100 text-amber-700' },
  in_progress: { label: 'In progress', tone: 'bg-blue-100 text-blue-700' },
  resolved:    { label: 'Resolved',    tone: 'bg-green-100 text-green-700' },
};

// MySQL returns IST wall-clock strings; tag +05:30 so they render correctly anywhere.
const fmtTicketDate = (s) => {
  if (!s) return '';
  const d = new Date(s.replace(' ', 'T') + '+05:30');
  return isNaN(d) ? '' : d.toLocaleString('en-IN', { timeZone: 'Asia/Kolkata', day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
};

export default function SupportSection({ course, user, onCourseUpdate }) {
  const resources = course?.support_resources || [];
  const tickets = course?.support_tickets || [];
  const [sendError, setSendError] = useState('');
  const [openTicket, setOpenTicket] = useState(null);
  const [openResource, setOpenResource] = useState(null);
  // Group by category, keeping the server's order (category ASC, newest first).
  const resourceGroups = resources.reduce((groups, r) => {
    const key = r.category || 'General';
    (groups[key] ||= []).push(r);
    return groups;
  }, {});
  const [openFaq, setOpenFaq] = useState(null);
  const [form, setForm] = useState({ subject: '', message: '', priority: 'normal' });
  const [sent, setSent] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [confirmLoading, setConfirmLoading] = useState(false);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [bookedSlot, setBookedSlot] = useState(null);
  const [showSlotConfirm, setShowSlotConfirm] = useState(false);
  const [copiedEmail, setCopiedEmail] = useState(false);
  const [activeTab, setActiveTab] = useState('contact');

  const tabs = [
    { id: 'contact', label: 'Contact Us' },
    { id: 'schedule', label: 'Schedule Meeting' },
    { id: 'resources', label: resources.length ? `Resources (${resources.length})` : 'Resources' },
    { id: 'faq', label: 'FAQs' },
  ];

  const handleSendClick = () => {
    if (form.subject && form.message) setShowConfirm(true);
  };

  const handleConfirmSend = async () => {
    setConfirmLoading(true);
    setSendError('');
    try {
      const { data } = await axios.post(
        'https://xceededge.com/support-ticket.php',
        {
          action: 'create',
          session: user?.session,
          course_id: course?.id,
          subject: form.subject,
          message: form.message,
          priority: form.priority,
        },
        { headers: { 'Content-Type': 'application/json' } }
      );
      if (!data?.success) {
        // get-user.php answers a bad/expired session with { error }, not { success:false }
        throw new Error(data?.message || (data?.error ? 'Your session has expired — please log in again.' : 'Could not send your message.'));
      }
      onCourseUpdate?.({ support_tickets: [data.ticket, ...tickets] });
      setSent(true);
      setForm({ subject: '', message: '', priority: 'normal' });
      setTimeout(() => setSent(false), 5000);
    } catch (err) {
      setSendError(err?.response
        ? (err.response.data?.message && err.response.data.message !== 'Server error'
            ? err.response.data.message
            : 'Could not send your message. Please try again.')
        : (err?.message || 'Could not send your message.'));
    } finally {
      setConfirmLoading(false);
      setShowConfirm(false);
    }
  };

  const handleSlotSelect = (slot) => {
    if (!slot.available) return;
    setSelectedSlot(slot);
    setShowSlotConfirm(true);
  };

  const handleConfirmSlot = () => {
    setConfirmLoading(true);
    setTimeout(() => {
      setConfirmLoading(false);
      setShowSlotConfirm(false);
      setBookedSlot(selectedSlot);
      setSelectedSlot(null);
    }, 1000);
  };

  const copyEmail = () => {
    navigator.clipboard.writeText('info@xceededge.com');
    setCopiedEmail(true);
    setTimeout(() => setCopiedEmail(false), 2000);
  };

  return (
    <div className="space-y-5">
      <div>
        <h2 className="font-heading text-2xl font-bold tracking-tight">Support Center</h2>
        <p className="text-muted-foreground text-sm mt-1">We're here to help you succeed — fast and personal.</p>
      </div>

      {/* Status Bar */}
      <div className="glass rounded-2xl border border-white/80 p-4 flex flex-wrap items-center gap-4">
        <div className="flex items-center gap-2">
          <motion.span animate={{ scale: [1, 1.4, 1] }} transition={{ duration: 1.5, repeat: Infinity }}
            className="w-2 h-2 rounded-full bg-green-500 block" />
          <span className="text-sm font-semibold text-green-700">Support Online</span>
        </div>
        <span className="text-muted-foreground/40 hidden sm:block">|</span>
        <div className="flex items-center gap-1.5 text-sm text-muted-foreground">
          <Clock className="w-3.5 h-3.5 text-primary" />
          Avg. Response: <span className="font-bold text-foreground ml-1">&lt; 2 hours</span>
        </div>
        <span className="text-muted-foreground/40 hidden sm:block">|</span>
        <div className="flex items-center gap-1.5 text-sm text-muted-foreground">
          <Star className="w-3.5 h-3.5 text-primary fill-primary" />
          Support Rating: <span className="font-bold text-foreground ml-1">4.9/5</span>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 bg-black/5 p-1 rounded-2xl">
        {tabs.map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex-1 px-3 py-2 rounded-xl text-xs font-semibold transition-all ${
              activeTab === tab.id ? 'bg-white shadow-sm text-foreground' : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      <AnimatePresence mode="wait">
        <motion.div
          key={activeTab}
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -5 }}
          transition={{ duration: 0.18 }}
        >
          {/* CONTACT TAB */}
          {activeTab === 'contact' && (
            <div className="space-y-4">
              {/* Contact Cards */}
              <div className="grid sm:grid-cols-3 gap-3">
                {[
                  { icon: MessageCircle, title: 'Live Chat', subtitle: '9AM – 9PM IST', color: 'bg-blue-100 text-blue-600', action: 'Start Chat', href: '#' },
                  { icon: Mail, title: 'Email Support', subtitle: 'info@xceededge.com', color: 'bg-green-100 text-green-600', action: 'Copy Email', onClick: copyEmail },
                  { icon: Phone, title: 'Call Us', subtitle: '+91 98765 43210', color: 'bg-primary/15 text-primary', action: 'Call Now', href: 'tel:+919876543210' },
                ].map((opt, i) => (
                  <motion.div key={opt.title} initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.07 }}
                    whileHover={{ y: -3, transition: { duration: 0.2 } }}
                    className="glass rounded-2xl border border-white/80 p-5 text-center hover:border-primary/20 transition-colors group cursor-pointer">
                    <div className={`w-12 h-12 rounded-2xl ${opt.color} flex items-center justify-center mx-auto mb-3 group-hover:scale-110 transition-transform`}>
                      <opt.icon className="w-6 h-6" />
                    </div>
                    <p className="font-heading font-bold text-sm">{opt.title}</p>
                    <p className="text-xs text-muted-foreground mt-1">{opt.subtitle}</p>
                    {opt.href ? (
                      <a href={opt.href} target="_blank" rel="noopener noreferrer">
                        <Button size="sm" variant="outline" className="mt-3 rounded-xl text-xs font-semibold border-black/15 hover:bg-primary/10 w-full gap-1">
                          {opt.action} <ExternalLink className="w-3 h-3" />
                        </Button>
                      </a>
                    ) : (
                      <Button size="sm" variant="outline" onClick={opt.onClick}
                        className="mt-3 rounded-xl text-xs font-semibold border-black/15 hover:bg-primary/10 w-full gap-1">
                        {opt.title === 'Email Support' && copiedEmail ? (
                          <><CheckCircle2 className="w-3 h-3 text-green-500" /> Copied!</>
                        ) : (
                          <>{opt.action} <Copy className="w-3 h-3" /></>
                        )}
                      </Button>
                    )}
                  </motion.div>
                ))}
              </div>

              {/* Message Form */}
              <div className="glass rounded-2xl border border-white/80 p-6 space-y-4">
                <h3 className="font-heading font-bold text-base">Send a Message</h3>
                {sent ? (
                  <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} className="py-8 text-center">
                    <div className="w-14 h-14 rounded-2xl bg-green-100 flex items-center justify-center mx-auto mb-3">
                      <CheckCircle2 className="w-7 h-7 text-green-500" />
                    </div>
                    <p className="font-heading font-bold text-base text-green-600">Message Sent!</p>
                    <p className="text-sm text-muted-foreground mt-1">Our team will reply here — you'll see it under “Your requests”.</p>
                  </motion.div>
                ) : (
                  <>
                    {sendError && (
                      <div role="alert" className="flex items-start gap-2 text-sm text-red-700 bg-red-50 border border-red-200 rounded-xl px-3 py-2.5">
                        <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" /> {sendError}
                      </div>
                    )}
                    <div className="grid sm:grid-cols-2 gap-3">
                      <Input placeholder="Subject" value={form.subject}
                        onChange={e => setForm(f => ({ ...f, subject: e.target.value }))}
                        className="rounded-xl border-black/15 text-sm" />
                      <select
                        value={form.priority}
                        onChange={e => setForm(f => ({ ...f, priority: e.target.value }))}
                        className="rounded-xl border border-black/15 text-sm px-3 py-2 bg-background font-body"
                      >
                        <option value="normal">Normal Priority</option>
                        <option value="high">High Priority</option>
                        <option value="urgent">Urgent</option>
                      </select>
                    </div>
                    <Textarea placeholder="Describe your issue or question in detail..."
                      value={form.message}
                      onChange={e => setForm(f => ({ ...f, message: e.target.value }))}
                      className="rounded-xl border-black/15 text-sm h-28 resize-none" />
                    <Button onClick={handleSendClick} disabled={!form.subject || !form.message}
                      className="w-full gap-2 rounded-xl bg-primary text-primary-foreground font-bold shadow-lg shadow-primary/25 hover:bg-primary/90">
                      <Send className="w-4 h-4" /> Send Message
                    </Button>
                  </>
                )}
              </div>

              {/* Tickets this student has sent for this course, with status + reply */}
              {tickets.length > 0 && (
                <div className="glass rounded-2xl border border-white/80 p-6 space-y-3">
                  <h3 className="font-heading font-bold text-base">Your requests</h3>
                  {tickets.map(t => {
                    const st = TICKET_STATUS[t.status] || TICKET_STATUS.open;
                    const open = openTicket === t.id;
                    return (
                      <div key={t.id} className="border border-black/8 rounded-xl overflow-hidden">
                        <button onClick={() => setOpenTicket(open ? null : t.id)} aria-expanded={open}
                          className="w-full flex items-center gap-3 p-4 text-left hover:bg-primary/5 transition-colors">
                          <div className="flex-1 min-w-0">
                            <p className="font-heading font-semibold text-sm truncate">{t.subject}</p>
                            <p className="text-[11px] text-muted-foreground mt-0.5">
                              Sent {fmtTicketDate(t.created_at)}{t.admin_reply ? ' · Team replied' : ''}
                            </p>
                          </div>
                          <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full flex-shrink-0 ${st.tone}`}>{st.label}</span>
                          <ChevronDown className={`w-4 h-4 text-primary flex-shrink-0 transition-transform ${open ? 'rotate-180' : ''}`} />
                        </button>
                        <AnimatePresence initial={false}>
                          {open && (
                            <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }}>
                              <div className="px-4 pb-4 space-y-3">
                                <p className="text-sm text-muted-foreground whitespace-pre-line leading-relaxed">{t.message}</p>
                                {t.admin_reply ? (
                                  <div className="rounded-xl bg-primary/10 border border-primary/25 p-3">
                                    <p className="text-[11px] font-bold uppercase tracking-wide text-primary mb-1">
                                      Reply from XceedEdge{t.replied_at ? ` · ${fmtTicketDate(t.replied_at)}` : ''}
                                    </p>
                                    <p className="text-sm whitespace-pre-line leading-relaxed"><Linkified text={t.admin_reply} /></p>
                                  </div>
                                ) : (
                                  <p className="text-xs text-muted-foreground italic">Waiting for a reply from the team.</p>
                                )}
                              </div>
                            </motion.div>
                          )}
                        </AnimatePresence>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* SCHEDULE TAB */}
          {activeTab === 'schedule' && (
            <div className="space-y-4">
              <div className="glass rounded-2xl border border-white/80 p-6">
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-11 h-11 rounded-2xl bg-primary/15 flex items-center justify-center">
                    <Calendar className="w-5 h-5 text-primary" />
                  </div>
                  <div>
                    <h3 className="font-heading font-bold text-base">Book a 1:1 Mentor Session</h3>
                    <p className="text-xs text-muted-foreground mt-0.5">30 or 60 min sessions via Google Meet</p>
                  </div>
                </div>

                {bookedSlot ? (
                  <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }}
                    className="mt-5 p-5 rounded-2xl bg-green-50 border border-green-200 text-center">
                    <CheckCircle2 className="w-10 h-10 text-green-500 mx-auto mb-2" />
                    <p className="font-heading font-bold text-green-700">Session Booked!</p>
                    <p className="text-sm text-green-600 mt-1">{bookedSlot.time}</p>
                    <p className="text-xs text-muted-foreground mt-2">A Google Meet link has been sent to your email.</p>
                    <Button size="sm" variant="outline" onClick={() => setBookedSlot(null)}
                      className="mt-3 rounded-xl border-green-300 text-green-700 text-xs">
                      Book Another Session
                    </Button>
                  </motion.div>
                ) : (
                  <>
                    <p className="text-sm text-muted-foreground mt-4 mb-3 font-semibold">Available Slots:</p>
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                      {meetingSlots.map((slot, i) => (
                        <button
                          key={i}
                          onClick={() => handleSlotSelect(slot)}
                          disabled={!slot.available}
                          className={`p-3 rounded-xl text-xs font-semibold border transition-all ${
                            slot.available
                              ? 'border-primary/20 bg-primary/5 text-primary hover:bg-primary hover:text-primary-foreground hover:shadow-md hover:shadow-primary/20'
                              : 'border-black/10 bg-black/5 text-muted-foreground cursor-not-allowed'
                          }`}
                        >
                          {slot.available ? (
                            <span className="flex items-center justify-center gap-1.5">
                              <Calendar className="w-3 h-3" /> {slot.time}
                            </span>
                          ) : (
                            <span>{slot.time} · Booked</span>
                          )}
                        </button>
                      ))}
                    </div>
                    <p className="text-xs text-muted-foreground mt-3 flex items-center gap-1.5">
                      <Zap className="w-3 h-3 text-primary" />
                      Can't find a suitable time? Email us at info@xceededge.com for custom scheduling.
                    </p>
                  </>
                )}
              </div>

              <div className="glass rounded-2xl border border-white/80 p-5">
                <h3 className="font-heading font-bold text-sm mb-3">What to expect in your session</h3>
                <div className="space-y-2.5">
                  {[
                    'Personalized guidance on your learning goals',
                    'Code reviews and project feedback',
                    'Career and placement strategy',
                    'Resume and interview preparation',
                  ].map((item, i) => (
                    <div key={i} className="flex items-start gap-2.5 text-sm">
                      <CheckCircle2 className="w-4 h-4 text-primary flex-shrink-0 mt-0.5" />
                      <span className="text-muted-foreground">{item}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* RESOURCES TAB — added by the course team in the admin panel */}
          {activeTab === 'resources' && (
            resources.length === 0 ? (
              <div className="glass rounded-2xl border border-white/80 p-10 text-center">
                <div className="w-12 h-12 rounded-2xl bg-primary/15 flex items-center justify-center mx-auto mb-3">
                  <Inbox className="w-6 h-6 text-primary" />
                </div>
                <p className="font-heading font-bold text-sm">No resources yet</p>
                <p className="text-xs text-muted-foreground mt-1">
                  Your course team hasn't added support resources for this course. Reach out from the Contact Us tab anytime.
                </p>
              </div>
            ) : (
              <div className="space-y-5">
                {Object.entries(resourceGroups).map(([category, items]) => (
                  <div key={category} className="space-y-2.5">
                    <p className="text-[11px] uppercase tracking-[0.16em] font-semibold text-muted-foreground px-1">{category}</p>
                    {items.map((res, i) => {
                      const Icon = resourceIcon(res);
                      const open = openResource === res.id;
                      return (
                        <motion.div key={res.id} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }}
                          className={`glass rounded-2xl border overflow-hidden transition-colors ${open ? 'border-primary/30' : 'border-white/80 hover:border-primary/20'}`}>
                          <button
                            onClick={() => setOpenResource(open ? null : res.id)}
                            aria-expanded={open}
                            className="w-full p-4 flex items-center gap-4 text-left group"
                          >
                            <div className={`w-11 h-11 rounded-2xl flex items-center justify-center flex-shrink-0 transition-all ${open ? 'bg-primary shadow-lg shadow-primary/25' : 'bg-primary/15 group-hover:bg-primary/25'}`}>
                              <Icon className={`w-5 h-5 ${open ? 'text-primary-foreground' : 'text-primary'}`} />
                            </div>
                            <p className="flex-1 min-w-0 font-heading font-bold text-sm">{res.title}</p>
                            <ChevronDown className={`w-4 h-4 text-primary flex-shrink-0 transition-transform ${open ? 'rotate-180' : ''}`} />
                          </button>
                          <AnimatePresence initial={false}>
                            {open && (
                              <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }}>
                                <div className="px-4 pb-4 pl-[4.75rem] text-sm text-muted-foreground leading-relaxed whitespace-pre-line">
                                  <Linkified text={res.content} />
                                </div>
                              </motion.div>
                            )}
                          </AnimatePresence>
                        </motion.div>
                      );
                    })}
                  </div>
                ))}
              </div>
            )
          )}

          {/* FAQ TAB */}
          {activeTab === 'faq' && (
            <div className="glass rounded-2xl border border-white/80 p-6 space-y-2">
              {faqs.map((faq, i) => (
                <motion.div key={i} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.06 }}
                  className="border border-black/8 rounded-xl overflow-hidden">
                  <button onClick={() => setOpenFaq(openFaq === i ? null : i)}
                    className="w-full flex items-center justify-between p-4 text-left hover:bg-primary/5 transition-colors">
                    <span className="font-heading font-semibold text-sm">{faq.q}</span>
                    <ChevronDown className={`w-4 h-4 text-primary flex-shrink-0 transition-transform ${openFaq === i ? 'rotate-180' : ''}`} />
                  </button>
                  <AnimatePresence>
                    {openFaq === i && (
                      <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }}
                        className="px-4 pb-4 text-sm text-muted-foreground leading-relaxed">
                        {faq.a}
                      </motion.div>
                    )}
                  </AnimatePresence>
                </motion.div>
              ))}
            </div>
          )}
        </motion.div>
      </AnimatePresence>

      {/* Confirm Message Modal */}
      <ConfirmModal
        open={showConfirm}
        title="Send Support Message?"
        description={`You're sending a "${form.priority}" priority message: "${form.subject}". Our team's reply will appear under “Your requests”.`}
        confirmLabel="Send Message"
        loading={confirmLoading}
        onConfirm={handleConfirmSend}
        onCancel={() => setShowConfirm(false)}
      />

      {/* Confirm Slot Modal */}
      <ConfirmModal
        open={showSlotConfirm}
        title="Confirm Meeting Booking"
        description={`Book a 1:1 mentor session for ${selectedSlot?.time}? A Google Meet link will be sent to your email.`}
        confirmLabel="Book Session"
        loading={confirmLoading}
        onConfirm={handleConfirmSlot}
        onCancel={() => { setShowSlotConfirm(false); setSelectedSlot(null); }}
      />
    </div>
  );
}